import React, { useMemo, useState } from "react";
import {
    Badge,
    Button,
    FormLayout,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Modal,
    Panel,
    Select,
    Switch,
    Table,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import { useMutation } from "@apollo/react-hooks";
import {
    MUTATION_UPDATE_ONFLEET_APP,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import { onfleetAsignModeOptions } from "../../config/constant";
import _ from "lodash";
import {
    useFormActions,
    useForm,
    useMeta,
    useNotification,
    useValidator,
} from "@/hooks";
import OnFleetMerchantForm from "./OnFleetMerchantForm";
import { PlusIcon, RowActionsMenu, storeTableUiStyles } from "./shared";
import styles from "./GeneralsForm.module.scss";
import deliveryStyles from "./DeliverySchedules.module.scss";

const SaveIcon = () => <Icon icon="save" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const OnFleetForm = ({ app, loading: pageLoading }) => {
    const { user } = useMeta();
    const notify = useNotification();
    const [merchants, setMerchants] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [isCreate, setIsCreate] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [rowSelect, setRowSelect] = useState(null);
    const [isDelete, setIsDelete] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const { inputs, setInputs, inputChange } = useForm();

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
    }

    const options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...inputs,
                id: inputs.id,
                user: inputs.user,
                sandbox: inputs.sandbox,
                test_user: inputs.test_user,
                auto_assign: inputs.auto_assign,
                auto_assign_mode: inputs.auto_assign_mode,
                merchants: inputs.merchants,
                type: "OnFleet",
            },
        },
    };

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "id",
                method: "isEmpty",
                validWhen: false,
                message: "This app id is required.",
            },
            {
                field: "user",
                method: "isEmpty",
                validWhen: false,
                message: "This user is required.",
            },
            {
                field: "test_user",
                method: "isEmpty",
                validWhen: false,
                message: "This test user is required.",
            },
            {
                field: "auto_assign_mode",
                method: "isEmpty",
                validWhen: false,
                message: "This auto assign mode is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const [mutate] = useMutation(MUTATION_UPDATE_ONFLEET_APP, {
        refetchQueries: [],
        onCompleted: () => {
            handleCancel();
            setIsUpdating(false);
            setIsDelete(false);
            setShowDeleteConfirm(false);
            setInputs({ ...inputs, merchants });
            notify(
                `Setting Was ${
                    isCreate ? "Created" : !isDelete ? "Updated" : "Removed"
                } Successfully"`,
                "success",
            );
        },
        onError: () => {
            setIsUpdating(false);
            notify(
                `Error ${
                    isCreate ? "Updating" : !isDelete ? "Creating" : "Removing"
                }  The Merchant, If The Error Persist Please Contact Support.`,
                "error",
            );
        },
    });

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "OnFleet",
        "",
        MUTATION_UPDATE_ONFLEET_APP,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
        "primary",
    );

    const modeOptions = useMemo(
        () =>
            onfleetAsignModeOptions.map(option => ({
                ...option,
                value: String(option.value),
            })),
        [],
    );

    const rawMerchants = (inputs && inputs.merchants) || [];
    const rows = useMemo(
        () =>
            rawMerchants.map((merchant, index) => ({
                ...merchant,
                _index: index,
            })),
        [rawMerchants],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            disabled={isUpdating || showForm}
                            label={row.store_id || `merchant ${row._index + 1}`}
                            onDelete={() => {
                                setIsDelete(true);
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowDeleteConfirm(true);
                            }}
                            onUpdate={() => {
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowForm(true);
                            }}
                        />
                    </span>
                ),
            },
            {
                id: "store_id",
                header: "Store Id",
                minWidth: 180,
                renderCell: row =>
                    row.store_id ? (
                        <Badge variant="primary">{row.store_id}</Badge>
                    ) : (
                        ""
                    ),
            },
            {
                id: "merchant",
                header: "Merchant",
                minWidth: 220,
                renderCell: row => row.merchant || "",
            },
        ],
        [isUpdating, showForm],
    );

    const handlerUpdatingOrCreate = array => {
        setIsUpdating(true);
        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...inputs,
                    store_id: inputs.store_id,
                    merchants: array,
                },
            },
        });
    };

    const handleCancel = () => {
        setIsUpdating(false);
        setShowForm(false);
    };

    const confirmDelete = () => {
        const nextMerchants = _.filter(
            inputs.merchants,
            (merchant, index) => index !== rowSelect,
        );
        setMerchants(nextMerchants);
        setIsUpdating(true);
        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...inputs,
                    merchants: nextMerchants,
                },
            },
        });
    };

    const handleAssignModeChange = value => {
        setInputs({
            ...inputs,
            auto_assign_mode: value,
        });
    };

    return (
        <>
            <Modal
                description="Are You Sure You Want To Remove The Merchant ?"
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button
                            loading={isUpdating}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isUpdating}
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setIsDelete(false);
                            }}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) {
                        setShowDeleteConfirm(false);
                        setIsDelete(false);
                    }
                }}
                open={showDeleteConfirm}
                title="Remove merchant"
            >
                {null}
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>OnFleet</Heading>
                            <Text tone="secondary" variant="body-sm">
                                Onfleet Information
                            </Text>
                        </div>
                        <HeaderActions
                            actions={[
                                {
                                    id: "add-merchant",
                                    label: "Add Merchant",
                                    icon: <PlusIcon />,
                                    variant: "secondary",
                                    disabled: isUpdating || showForm,
                                    onClick: () => {
                                        setIsCreate(true);
                                        setShowForm(true);
                                    },
                                },
                                {
                                    id: "update",
                                    label: "Update",
                                    icon: loading ? undefined : <SaveIcon />,
                                    loading,
                                    disabled: loading || isUpdating,
                                    onClick: () => createOrUpdate(),
                                },
                            ]}
                            aria-label="OnFleet actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {user.loading || pageLoading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <FormLayout>
                        <FormLayout.Section title="Information">
                            <Switch
                                checked={Boolean(inputs.sandbox)}
                                label="Sandbox"
                                name="sandbox"
                                onChange={inputChange}
                            />
                        </FormLayout.Section>

                        <FormLayout.Section title="API Key">
                            <FormLayout.Grid columns={3}>
                                <Input
                                    error={fieldError(validator, "user")}
                                    fullWidth
                                    label="User"
                                    name="user"
                                    onChange={inputChange}
                                    value={inputs.user || ""}
                                />
                                <Input
                                    error={fieldError(validator, "test_user")}
                                    fullWidth
                                    label="Test User"
                                    name="test_user"
                                    onChange={inputChange}
                                    value={inputs.test_user || ""}
                                />
                                <Select
                                    error={fieldError(
                                        validator,
                                        "auto_assign_mode",
                                    )}
                                    fullWidth
                                    label="Auto Assign Mode"
                                    onValueChange={handleAssignModeChange}
                                    options={modeOptions}
                                    value={
                                        inputs.auto_assign_mode != null
                                            ? String(inputs.auto_assign_mode)
                                            : undefined
                                    }
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="Auto Assing">
                            <Switch
                                checked={Boolean(inputs.auto_assign)}
                                label="Auto Assign"
                                name="auto_assign"
                                onChange={inputChange}
                            />
                        </FormLayout.Section>

                        {showForm ? (
                            <OnFleetMerchantForm
                                app={inputs}
                                handleCancel={handleCancel}
                                handlerUpdatingOrCreate={
                                    handlerUpdatingOrCreate
                                }
                                isCreate={isCreate}
                                isUpdating={isUpdating}
                                selectMerchant={rowSelect}
                                setMerchants={setMerchants}
                            />
                        ) : null}

                        <FormLayout.Section title="Merchants">
                            <Table
                                columns={columns}
                                getRowId={row => String(row._index)}
                                rows={rows}
                            />
                        </FormLayout.Section>
                    </FormLayout>
                )}
            </Panel>
        </>
    );
};

export default OnFleetForm;
